import {
  ReviewPoolItem,
  ReviewSourceTag,
  ReviewDiagnosisType,
  ReviewContactPerson,
  toContactResult,
} from '../types/reviewPool';
import { ReviewPoolItemDto } from '../graphql/queries/getReviewPoolListing';

/**
 * 后端 ReviewPoolItemType（GraphQL）→ 前端 ReviewPoolItem 适配。
 * 解析 contactPersonJson、拼 displayAddress、映射 similarStations，保持组件消费的 mock 形状不变。
 */

interface RawContact {
  name?: string | null;
  countryCode?: string | number | null;
  phoneCountryCode?: string | number | null;
  phone?: string | null;
  inBlacklist?: boolean | null;
}

/** contactPersonJson 形如 [{name,countryCode,phone,extension,inBlacklist}]（pickup 写入）。全字段按可空处理。 */
function parseContacts(json: string | null): ReviewContactPerson[] {
  if (!json || !json.trim()) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.map((c: RawContact) => {
      const cc = c?.phoneCountryCode ?? c?.countryCode;
      return {
        name: c?.name ?? undefined,
        phoneCountryCode: cc != null && cc !== '' ? String(cc) : undefined,
        phone: c?.phone ?? undefined,
        inBlacklist: !!c?.inBlacklist,
      };
    });
  } catch {
    return [];
  }
}

/** channelIdsJson 形如 [123,456]（pickup 写入的渠道 id 数组）。解析失败返回 []。 */
function parseChannelIds(json: string | null): number[] {
  if (!json || !json.trim()) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}

/** 标准化 + dock 拼接展示地址：line1[, line2][, dock], city, ST zip，跳过空段。 */
function buildDisplayAddress(dto: ReviewPoolItemDto): string {
  const parts: string[] = [];
  const push = (v?: string | null) => {
    if (v && v.trim()) parts.push(v.trim());
  };
  push(dto.addressLine1);
  push(dto.addressLine2);
  push(dto.dock);
  push(dto.city);
  const stateZip = [dto.state?.trim(), dto.zipcode?.trim()].filter(Boolean).join(' ');
  if (stateZip) parts.push(stateZip);
  return parts.join(', ');
}

export function toReviewPoolItem(dto: ReviewPoolItemDto): ReviewPoolItem {
  return {
    id: dto.id ?? 0,
    warehouseId: dto.warehouseId ?? 0,
    displayAddress: buildDisplayAddress(dto),
    addressLine1: dto.addressLine1 ?? undefined,
    addressLine2: dto.addressLine2 ?? undefined,
    city: dto.city ?? undefined,
    state: dto.state ?? undefined,
    zipcode: dto.zipcode ?? undefined,
    dock: dto.dock ?? undefined,
    contactPersons: parseContacts(dto.contactPersonJson),
    // 渠道名由 api-server 按 channelIdsJson 批量解析回填（见 PickupReviewPoolQuery.enrichChannelNames）
    channelNames: dto.channelNames ?? [],
    channelIds: parseChannelIds(dto.channelIdsJson),
    sourceTag: (dto.sourceTag ?? ReviewSourceTag.System) as ReviewSourceTag,
    diagnosisType: (dto.diagnosisType ?? ReviewDiagnosisType.New) as ReviewDiagnosisType,
    standardizeStatus: dto.standardizeStatus ?? undefined,
    similarCount: dto.similarCount,
    similarStations: (dto.similarStations ?? []).map((s) => ({
      id: s.id ?? 0,
      name: s.name ?? undefined,
      address: s.address ?? '',
      contact: s.contact ?? undefined,
      // 不兜默认值：null 表示「该站点不在 2.0 体系内」，与「link_status=1 待上门」是两回事，
      // 兜成 0 或 1 会让选择器给出错误标注
      linkStatus: s.linkStatus ?? undefined,
    })),
    relatedWarehouseId: dto.relatedWarehouseId ?? undefined,
    relatedWarehouseName: dto.relatedWarehouseName ?? undefined,
    firstOrderTime: dto.createdAt ?? '',
    // 无 analysisHash（如标准化失败）时以 id 兜底，避免多条塌进同一分组
    addressAnalysisHash: dto.addressAnalysisHash ?? `id-${dto.id ?? ''}`,
    // 不兜底：core_hash 为空时必须让下游看见 undefined（2.1 建站要靠它，兜个假值反而写脏 hash）
    addressCoreHash: dto.addressCoreHash ?? undefined,
    hasTodayAppointment: dto.hasTodayAppointment,
    todayOrderCount: dto.todayOrderCount,
    todayAppointmentId: dto.todayAppointmentId ?? undefined,
    // 草稿：结果码 1/2 转字面量；keys/urls 后端保证等长同序，这里不做补齐——
    // 长度不等时 ImageUploader 会整组不渲染（fileListFromProps 的第一个分支要求等长），
    // 那是后端映射出了问题，静默补齐只会把问题藏起来。
    contactDraft: {
      contact: toContactResult(dto.contactResult),
      proofKeys: dto.proofS3Keys ?? [],
      proofUrls: dto.proofDisplayUrls ?? [],
      secondContact: toContactResult(dto.secondContactResult),
      secondProofKeys: dto.secondProofS3Keys ?? [],
      secondProofUrls: dto.secondProofDisplayUrls ?? [],
    },
    // 地址异常上报（2.2 需求点1）：**以 abnormalReportedAt 有无值为唯一判据**。
    // 不用"原因非空"或"凭证非空"代替：原因可为空（历史数据/后端兜底），凭证本就允许不传，
    // 用它们判会让"已上报但没传凭证"的条目在界面上表现为未上报，运营会重复上报一次。
    abnormalReport: dto.abnormalReportedAt
      ? {
          reportedAt: dto.abnormalReportedAt,
          reason: dto.abnormalReason ?? undefined,
          remark: dto.abnormalRemark ?? undefined,
          proofKeys: dto.abnormalProofS3Keys ?? [],
          proofUrls: dto.abnormalProofDisplayUrls ?? [],
          reportedByName: dto.abnormalReportedByName ?? undefined,
        }
      : undefined,
  };
}
